import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { api } from "../axios/api";
import { numberFormat } from "../config/Config";
import {
  Package, Layers, AlertTriangle, ShieldAlert,
  TrendingUp, ArrowDownRight, ArrowUpRight, DollarSign, Archive
} from "lucide-react";
import Loader from "../components/Loader";

const DashboardProdutos = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodoAtivo, setPeriodoAtivo] = useState("mensal");

  useEffect(() => {
    const fetchProductData = async () => {
      if (!empresaId) return;
      try {
        setLoading(true);
        const res = await api.get(`/app/dashboard/produtos/${empresaId}`);
        setData(res.data);
      } catch (err) {
        console.error("Erro ao carregar métricas de stock:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProductData();
  }, [empresaId]);

  if (loading) return <Loader />;

  const { cadastros = {}, financeiroStock = {}, fluxoEstoque = {}, graficos = {}, listas = {} } = data || {};
  const fluxoAtivo = fluxoEstoque[periodoAtivo] || { entradas: 0, saidas: 0 };

  // 📈 Gráfico 1: Rentabilidade por Produto (Top Faturamento)
  const rentabilidadeOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '45%' } },
    colors: ["#3b82f6"],
    dataLabels: { enabled: true, formatter: (v) => `${numberFormat(v, 0, ",", ".")} Kz`, style: { fontSize: '11px' } },
    xaxis: { categories: graficos.produtosMaisRentaveis?.map(p => p.Produto?.nome || "Inexistente") || [] }
  };

  // 📊 Gráfico 2: Mix de Vendas por Unidades (Donut)
  const unidadeMixOptions = {
    chart: { fontFamily: 'Inter, system-ui, sans-serif' },
    labels: graficos.vendasPorUnidade?.map(u => u.unidade_medida === "Nenhuma" ? "Unidades Indefinidas" : u.unidade_medida) || [],
    colors: ["#7c3aed", "#10b981", "#f59e0b", "#ef4444", "#06b6d4"],
    legend: { position: 'bottom' }
  };

  return (
    <div id="layout-wrapper" className="dashboard-produtos">
      <Navbar />
      <Sidebar />

      <style>{`
        .dashboard-produtos { background-color: #f8fafc; min-height: 100vh; }
        .app-wrapper { padding: 24px; }
        .metric-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; transition: all 0.2s ease; }
        .metric-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
        .period-btn { padding: 6px 16px; font-size: 13px; font-weight: 600; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; transition: all 0.2s; }
        .period-btn.active { background: #3b82f6; color: #fff; border-color: #3b82f6; }
        .card-title-flat { font-size: 13px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; }
        .table-clean th { font-size: 11px; color: #64748b; text-transform: uppercase; padding: 12px 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
        .table-clean td { padding: 14px 16px; font-size: 13px; color: #334155; border-bottom: 1px solid #f1f5f9; }
      `}</style>

      <main className="app-wrapper">
        <div className="container-fluid">
          
          {/* Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h2 className="fw-bold text-slate-800 m-0" style={{ fontSize: "26px" }}>Gestão de Stocks & Inventário</h2>
              <small className="text-muted">Análise de valorização, fluxo de movimentação de mercadorias e auditorias de rutura.</small>
            </div>
            
            <div className="d-flex gap-1 bg-white p-1 rounded border">
              <button onClick={() => setPeriodoAtivo("diario")} className={`period-btn ${periodoAtivo === "diario" ? "active" : ""}`}>Hoje</button>
              <button onClick={() => setPeriodoAtivo("semanal")} className={`period-btn ${periodoAtivo === "semanal" ? "active" : ""}`}>Últimos 7 dias</button>
              <button onClick={() => setPeriodoAtivo("mensal")} className={`period-btn ${periodoAtivo === "mensal" ? "active" : ""}`}>Mês Atual</button>
            </div>
          </div>

          {/* 📊 SEÇÃO 1: INVENTÁRIO FINANCEIRO (PREÇO DE CUSTO VS VENDA) */}
          <div className="row g-3 mb-4">
            {[
              { label: `Investimento em Stock (Preço Custo)`, valor: `${numberFormat(financeiroStock.valorCusto, 2, ",", ".")} Kz`, icon: <Archive size={20} />, color: "#64748b" },
              { label: `Retorno Estimado (Preço Venda)`, valor: `${numberFormat(financeiroStock.valorVenda, 2, ",", ".")} Kz`, icon: <DollarSign size={20} />, color: "#3b82f6" },
              { label: `Margem de Lucro Estimada`, valor: `${numberFormat(financeiroStock.lucroEstimado, 2, ",", ".")} Kz`, icon: <TrendingUp size={20} />, color: "#10b981" },
              { label: `Movimentação no Período`, valor: `${numberFormat(fluxoAtivo.saidas, 0, ",", ".")} saídas`, icon: <ArrowDownRight size={20} />, color: "#ef4444" },
            ].map((item, i) => (
              <div key={i} className="col-12 col-sm-6 col-xl-3">
                <div className="metric-card" style={{ borderTop: `4px solid ${item.color}` }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted small fw-semibold text-uppercase">{item.label}</span>
                    <div style={{ color: item.color }}>{item.icon}</div>
                  </div>
                  <h3 className="fw-bold m-0 text-slate-700">{item.valor}</h3>
                </div>
              </div>
            ))}
          </div>

          {/* 📦 SEÇÃO 2: QUANTITATIVOS DE CADASTRO E ALERTAS OPERACIONAIS */}
          <div className="row g-3 mb-4">
            {[
              { label: "Catálogo Geral (Mix)", valor: cadastros.totalProdutos, icon: <Package size={18} />, badge: "Geral", color: "bg-slate-100 text-slate-600" },
              { label: "Artigos Físicos em Base", valor: cadastros.totalArtigos, icon: <Layers size={18} />, badge: "Stockável", color: "bg-blue-100 text-primary" },
              { label: "Produtos em Rutura Crítica (<=5)", valor: cadastros.produtosStockRutura, icon: <AlertTriangle size={18} />, badge: "Reposição", color: cadastros.produtosStockRutura > 0 ? "bg-danger-subtle text-danger animate-pulse" : "bg-light text-muted" },
              { label: "Lotes Próximos do Vencimento", valor: cadastros.produtosAExpirar, icon: <ShieldAlert size={18} />, badge: "90 dias", color: "bg-warning-subtle text-warning" },
            ].map((card, idx) => (
              <div key={idx} className="col-12 col-sm-6 col-md-3">
                <div className="metric-card bg-white d-flex align-items-center justify-content-between py-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className={`p-2 rounded ${card.color}`}>
                      {card.icon}
                    </div>
                    <div>
                      <span className="text-muted d-block" style={{ fontSize: "11px" }}>{card.label}</span>
                      <span className="fw-bold fs-6 text-slate-800">{card.valor || 0}</span>
                    </div>
                  </div>
                  <span className="badge bg-light text-dark border rounded-sm" style={{ fontSize: '10px' }}>{card.badge}</span>
                </div>
              </div>
            ))}
          </div>

          {/* 📈 SEÇÃO 3: GRÁFICOS */}
          <div className="row g-4 mb-4">
            <div className="col-xl-7">
              <div className="metric-card h-100">
                <div className="mb-3"><span className="card-title-flat">Ranking de Produtos por Maior Faturamento</span></div>
                <ReactApexChart type="bar" height={280} series={[{ name: "Volume Faturado", data: graficos.produtosMaisRentaveis?.map(p => parseFloat(p.faturamento_total)) || [] }]} options={rentabilidadeOptions} />
              </div>
            </div>
            <div className="col-xl-5">
              <div className="metric-card h-100">
                <div className="mb-3"><span className="card-title-flat">Giro de Vendas por Tipo de Embalagem (Unidade)</span></div>
                <div className="d-flex justify-content-center align-items-center h-100">
                  {graficos.vendasPorUnidade && graficos.vendasPorUnidade.length > 0 ? (
                    <ReactApexChart type="donut" width="100%" height={260} series={graficos.vendasPorUnidade.map(u => parseFloat(u.total_faturado))} options={unidadeMixOptions} />
                  ) : (
                    <span className="text-muted small">Sem histórico de movimentação comercial.</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 🧾 SEÇÃO 4: LOGÍSTICA DE REPOSIÇÃO E AUDITORIA */}
          <div className="row g-4">
            <div className="col-xl-6">
              <div className="metric-card p-0 h-100">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                  <span className="card-title-flat m-0 text-danger">⚠️ Alertas de Reposição Urgente (Stock Crítico)</span>
                  <Link to="/produtos" className="btn btn-sm btn-light border fw-semibold text-danger" style={{ fontSize: "11px" }}>Emitir O.C</Link>
                </div>
                <div className="table-responsive">
                  <table className="table-clean w-100 m-0">
                    <thead>
                      <tr>
                        <th>Artigo</th>
                        <th>Armazém</th>
                        <th className="text-end">Quantidade Atual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listas.produtosCriticos && listas.produtosCriticos.length > 0 ? (
                        listas.produtosCriticos.map((prod, i) => (
                          <tr key={i}>
                            <td className="fw-semibold text-slate-700">{prod.nome}</td>
                            <td><span className="badge bg-light text-dark border">{prod.Armazem?.nome || "Principal"}</span></td>
                            <td className="text-end fw-bold text-danger">
                              {numberFormat(prod.quantidade, 2, ",", ".")} {prod.unidade}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted py-4 small">Parabéns! Não existem produtos em rutura.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="metric-card p-0 h-100">
                <div className="p-3 border-bottom">
                  <span className="card-title-flat m-0">Histórico Recente de Entradas & Saídas</span>
                </div>
                <div className="table-responsive">
                  <table className="table-clean w-100 m-0">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Operação</th>
                        <th>Qtd</th>
                        <th className="text-end">Operador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listas.ultimosMovimentos && listas.ultimosMovimentos.length > 0 ? (
                        listas.ultimosMovimentos.map((mov, i) => (
                          <tr key={i}>
                            <td>
                              <span className="fw-semibold d-block text-slate-700">{mov.Produto?.nome}</span>
                              <small className="text-muted" style={{ fontSize: '10px' }}>{new Date(mov.createdAt).toLocaleString()}</small>
                            </td>
                            <td>
                              <span className={`badge ${mov.tipo_movimento === 'Entrada' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                {mov.tipo_movimento === 'Entrada' ? <ArrowUpRight size={10} className="me-1" /> : <ArrowDownRight size={10} className="me-1" />}
                                {mov.tipo_movimento}
                              </span>
                            </td>
                            <td className="fw-bold">{numberFormat(mov.quantidade, 2, ",", ".")}</td>
                            <td className="text-end text-muted small">{mov.User?.nome}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-4 small">Nenhum ajuste ou entrada registada até ao momento.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </main>
    </div>
  );
};

export default DashboardProdutos;